import type {MarkdownEditorConfig, Subject} from '@github-ui/markdown-editor'
import type {ReactionViewerUser$key} from '@github-ui/reaction-viewer'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {useToastContext} from '@github-ui/toast'
import {Box} from '@primer/react'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {graphql, type PreloadedQuery, useRelayEnvironment} from 'react-relay'
import {useFragment, usePreloadedQuery} from 'react-relay/hooks'

import type {IssueBody$key} from './__generated__/IssueBody.graphql'
import type {IssueBodyContent$key} from './__generated__/IssueBodyContent.graphql'
import type {IssueBodyQuery} from './__generated__/IssueBodyQuery.graphql'
import {IssueBodyEditor} from './components/IssueBodyEditor'
import {IssueBodyHeader} from './components/IssueBodyHeader'
import {IssueBodyViewer} from './components/IssueBodyViewer'
import {ERRORS, LABELS, TEST_IDS} from './constants'
import {useTaskListBlock} from './hooks/useTaskListBlock'
import {commitUpdateIssueBodyMutation} from './mutations/update-issue-body-mutation'

export type {IssueBody$data} from './__generated__/IssueBody.graphql'
export type {IssueBodyContent$data} from './__generated__/IssueBodyContent.graphql'
export type {IssueBodyQuery} from './__generated__/IssueBodyQuery.graphql'
export type {IssueBodyHeaderAuthor$data} from './components/__generated__/IssueBodyHeaderAuthor.graphql'
export {IssueBodyLoading} from './components/IssueBodyLoading'
export {IssueBodyMarkdownViewer, IssueMarkdownContext} from './components/IssueBodyMarkdownViewer'
export type {
  CommentAuthorAssociation,
  updateIssueCommentBodyMutation_Comment$data,
  updateIssueCommentBodyMutation_Comment$key,
} from './mutations/__generated__/updateIssueCommentBodyMutation_Comment.graphql'
export {
  commitUpdateIssueCommentBodyMutation,
  updateCommentFragment,
} from './mutations/update-issue-comment-body-mutation'

export const IssueBodyGraphqlQuery = graphql`
  query IssueBodyQuery($owner: String!, $repo: String!, $number: Int!) {
    repository(owner: $owner, name: $repo) {
      issue(number: $number) {
        ...IssueBody
        # eslint-disable-next-line relay/must-colocate-fragment-spreads TODO: fix this
        ...IssueCommentComposer
        # eslint-disable-next-line relay/must-colocate-fragment-spreads TODO: fix this
        ...IssueActions
      }
    }
    viewer {
      ...ReactionViewerUser
    }
  }
`

const issueBodyContentFragment = graphql`
  fragment IssueBodyContent on Issue {
    body
    bodyHTML(unfurlReferences: true, renderTasklistBlocks: true)
  }
`

type IssueBodyProps = {
  issuesQueryRef: PreloadedQuery<IssueBodyQuery>
  onLinkClick?: (event: MouseEvent) => void
  onIssueUpdate?: () => void
  markdownEditorConfig?: MarkdownEditorConfig
  startIssueEdit: () => void
  endIssueEdit?: () => void
  isIssueEditActive?: boolean
}
type IssueBodyInternalProps = Omit<IssueBodyProps, 'issuesQueryRef'> & {
  issue: IssueBody$key
  viewer: ReactionViewerUser$key
}

export function IssueBody({issuesQueryRef, ...props}: IssueBodyProps) {
  const preloadedData = usePreloadedQuery<IssueBodyQuery>(IssueBodyGraphqlQuery, issuesQueryRef)

  return (
    preloadedData.repository &&
    preloadedData.repository.issue && (
      <IssueBodyInternal issue={preloadedData.repository.issue} viewer={preloadedData.viewer} {...props} />
    )
  )
}

export function IssueBodyInternal({
  issue,
  onLinkClick,
  viewer,
  markdownEditorConfig,
  onIssueUpdate,
  startIssueEdit,
  endIssueEdit,
  isIssueEditActive,
}: IssueBodyInternalProps) {
  const data = useFragment(
    graphql`
      fragment IssueBody on Issue {
        id
        databaseId
        createdAt
        author {
          ...IssueBodyHeaderAuthor
        }
        repository {
          databaseId
          nameWithOwner
          slashCommandsEnabled
          id
        }
        title
        number
        viewerCanUpdateNext
        locked
        # eslint-disable-next-line relay/must-colocate-fragment-spreads TODO: fix this
        ...ReactionViewerGroups
        ...IssueBodyContent
        ...MarkdownEditHistoryViewer_comment
      }
    `,
    issue,
  )

  const issueId = data.id
  const repositoryDatabaseId = data.repository.databaseId!

  const [bodyIsStale, setBodyIsStale] = useState(false)
  const [body, setBody] = useState<string>('')
  const [isIssueBodyEditActive, setIsIssueBodyEditActive] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [defferedIsEditing, setDefferedIsEditing] = useState(false)
  const relayEnvironment = useRelayEnvironment()

  const {addToast} = useToastContext()

  useEffect(() => {
    if (!isIssueEditActive) {
      setIsIssueBodyEditActive(false)
    }
  }, [isIssueEditActive])

  const subject = useMemo<Subject>(() => {
    return {
      type: 'issue',
      id: {
        id: data.id,
        databaseId: data.databaseId!,
      },
      repository: {
        databaseId: repositoryDatabaseId,
        nwo: data.repository.nameWithOwner,
        slashCommandsEnabled: data.repository.slashCommandsEnabled,
      },
    }
  }, [
    data.databaseId,
    data.id,
    data.repository.nameWithOwner,
    data.repository.slashCommandsEnabled,
    repositoryDatabaseId,
  ])

  const {body: issueBody, bodyHTML} = useFragment(issueBodyContentFragment, data as IssueBodyContent$key)

  const {viewerRef, onStartEdit, snapshot, isTasklistDirty} = useTaskListBlock({
    id: issueId,
    markdown: body,
    setMarkdown: setBody,
    html: bodyHTML,
    isEditing: defferedIsEditing,
  })

  useEffect(() => {
    if (isSubmitting) return

    if (isIssueBodyEditActive || isTasklistDirty) {
      return
    }

    setBody(issueBody)

    return () => {
      setBodyIsStale(false)
    }
  }, [issueBody, bodyHTML, setBody, isIssueBodyEditActive, body, isSubmitting, isTasklistDirty])

  useEffect(() => setIsIssueBodyEditActive(false), [issueId])

  useEffect(() => {
    setDefferedIsEditing(isIssueBodyEditActive)
    if (isIssueBodyEditActive) {
      onStartEdit()
    }
  }, [isIssueBodyEditActive, onStartEdit])

  const commitIssueBodyEdit = useCallback(() => {
    commitUpdateIssueBodyMutation({
      environment: relayEnvironment,
      input: {issueId, body},
      onError: () => {
        addToast({
          type: 'error',
          message: ERRORS.couldNotUpdateIssueBody,
        })
      },
      onCompleted: () => {
        onIssueUpdate?.()
        setIsIssueBodyEditActive(false)
        setIsSubmitting(false)
      },
    })
  }, [relayEnvironment, issueId, body, addToast, onIssueUpdate])

  const displayHtml = snapshot.length > 0 ? snapshot : bodyHTML

  return (
    <Box
      ref={viewerRef}
      sx={{
        flexGrow: 1,
        order: [1, 1, 1, 1, 0],
      }}
      data-testid={TEST_IDS.issueBody}
      className={'react-issue-body'}
      data-hpc
    >
      <h2 className="sr-only">{LABELS.issueBodyHeader}</h2>
      {defferedIsEditing || isSubmitting ? (
        <IssueBodyEditor
          editorDisabled={isSubmitting}
          trailingIcon={!isSubmitting}
          subjectId={issueId}
          subject={subject}
          body={body}
          bodyIsStale={bodyIsStale}
          onChange={setBody}
          onCancel={() => {
            setIsIssueBodyEditActive(false)
            setBody(issueBody)
            endIssueEdit?.()
          }}
          onCommit={() => {
            setIsSubmitting(true)
            commitIssueBodyEdit()
            endIssueEdit?.()
          }}
          markdownEditorConfig={markdownEditorConfig}
        />
      ) : (
        <Box sx={{display: 'flex', flexDirection: 'row', gap: 2}}>
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'border.muted',
              boxShadow: 'shadow.small',
              borderRadius: 2,
              flexGrow: 1,
              width: '100%',
              minWidth: 0,
            }}
          >
            <IssueBodyHeader
              author={data.author}
              createdAt={data.createdAt}
              editHistory={data}
              viewerCanUpdate={data.viewerCanUpdateNext || false}
              startIssueBodyEdit={() => {
                startIssueEdit()
                setIsIssueBodyEditActive(true)
              }}
              editInProgress={!!isIssueEditActive}
            />
            <IssueBodyViewer
              id={issueId}
              viewerRef={viewerRef}
              html={displayHtml as SafeHTMLString}
              markdown={issueBody}
              viewerCanUpdate={data.viewerCanUpdateNext || false}
              reactionGroups={data}
              viewer={viewer}
              onLinkClick={onLinkClick}
              locked={data.locked}
            />
          </Box>
        </Box>
      )}
    </Box>
  )
}

try{ IssueBody.displayName ||= 'IssueBody' } catch {}
try{ IssueBodyInternal.displayName ||= 'IssueBodyInternal' } catch {}